import { FC, ReactNode } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material'
import { styled } from '@mui/styles'

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
}))

interface TableColumn {
  key: string
  label: string
  align?: 'left' | 'center' | 'right'
  isStatement?: boolean
}

interface TableData {
  [key: string]: any
}

interface BeheardReportTableToCardsProps {
  columns: TableColumn[]
  data: TableData[]
  title?: string
  renderCell?: (column: TableColumn, row: TableData) => ReactNode
}

export const BeheardReportTableToCards: FC<BeheardReportTableToCardsProps> = ({
  columns,
  data,
  renderCell,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (!isMobile) {
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} align={column.align || 'left'} sx={{ padding: '8px 12px' }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index}>
                {columns.map((column) => (
                  <TableCell key={column.key} align={column.align || 'left'} sx={{ padding: '8px 12px' }}>
                    {renderCell ? renderCell(column, row) : row[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  const nonStatementColumns = columns.filter(col => !col.isStatement)
  const statementColumn = columns.find(col => col.isStatement)

  return (
    <Box>
      <Grid container spacing={2}>
        {data.map((row, index) => (
          <Grid item xs={12} key={index}>
            <StyledCard>
              <CardContent>
                {statementColumn && (
                  <Box mb={3}>
                    <Typography variant="subtitle2" color="textSecondary" mb={1}>
                      {statementColumn.label}
                    </Typography>
                    <Typography variant="body2">
                      {renderCell ? renderCell(statementColumn, row) : row[statementColumn.key]}
                    </Typography>
                  </Box>
                )}
                <Divider style={{ margin: '16px 0' }} />
                <Grid container spacing={2}>
                  {nonStatementColumns.map((column, idx) => (
                    <Grid item xs={6} key={`${column.key}-${idx}`}>
                      <Box textAlign="center">
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          {column.label}
                        </Typography>
                        <Typography variant="body2">
                          {renderCell ? renderCell(column, row) : row[column.key]}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}
